import { useEffect, useState } from 'react';
import { stopImpersonation } from '../lib/api.js';
import { useImpersonation, useSession } from '../lib/session.js';

/**
 * The bar an admin sees across the top of every screen while acting as someone else.
 *
 * It is not decoration and it is not dismissible: for as long as it is up, every edit,
 * delete and purchase on screen is landing in a real customer's account. It sits outside
 * the router so it survives navigation, and outside both shells so it is the same bar in
 * the app and in the console.
 */
export function ImpersonationBar() {
  const impersonation = useImpersonation();
  const session = useSession();
  const [leaving, setLeaving] = useState(false);
  const active = impersonation != null;

  // The shells are built on 100dvh and two sticky elements at top: 0. Rather than thread a
  // height through all of them, the bar publishes its own and the stylesheet subtracts it.
  useEffect(() => {
    if (!active) return;
    document.body.classList.add('is-impersonating');
    return () => document.body.classList.remove('is-impersonating');
  }, [active]);

  if (impersonation == null) return null;

  return (
    <div className="impersonation-bar" role="status">
      <span className="impersonation-dot" aria-hidden="true" />
      <span className="impersonation-text">
        You are impersonating <strong>{session.user.name}</strong>
        <span className="impersonation-email"> · {session.user.email}</span>
      </span>
      <span className="spacer" />
      <button
        type="button"
        className="btn impersonation-exit"
        // Spelled out even when the narrow layout shows only "Exit", so the control has
        // one stable name whatever the width.
        aria-label={`Exit to ${impersonation.adminName}`}
        disabled={leaving}
        onClick={() => {
          setLeaving(true);
          // A full load rather than a route change, for the same reason signing out does
          // one: the household manager, its sockets and every open document belong to the
          // person being left behind. Landing on the console is the useful destination —
          // an agent who has finished with one account is usually off to the next.
          void stopImpersonation()
            .then(() => window.location.assign('/admin/users'))
            .catch(() => setLeaving(false));
        }}
      >
        {leaving ? (
          'Exiting…'
        ) : (
          <>
            Exit<span className="impersonation-exit-name"> to {impersonation.adminName}</span>
          </>
        )}
      </button>
    </div>
  );
}
